import React, { useContext, useEffect, useReducer, useRef, useState } from "react";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarcode } from "@fortawesome/free-solid-svg-icons";
import * as notify from "../../../../shared/notify";
import eventEmitter from "../../../../shared/events";

import LoaderButton from "../../../loader/loader-button";
import Lockable from "../../../form-inputs/shared/lockable";
import PackageHierarchy from "./package-hierarchy";
import SuggestedCvid from "./suggested-cvid";
import withValidate from "../../../form-inputs/shared/validate";
//import ParentChild  from './parent-child';

import { reducer, initialState } from "../reducer";
import { cvidLookup, getPackageLevels } from "../lib/upcData";
import { Input, Select, Checkbox, InputList, Picklist } from "components/form-inputs/inputs";
import { UpcImageUpload } from "./imageUpload";
import { KEYCODE } from "../../../../shared/constants";
import { ViewOnlyPermissionsContext } from "contexts/view-only-permissions/view-only-permissions";
import { UpcsContext, ShipmentContext } from "../../contexts";
import { SettingsContext } from "contexts/settings";
import { DEFAULT_GENERIC_ERROR } from "../../../../shared/constants";
import { addProductToShipment, updateProduct } from "../lib/upcData";
import { getProductLimits, getShipment, getSLACounts } from "../../lib/shipmentData";
import { dialogConfirm } from "../../../../shared/dialog";
import { OverrideDates } from "../../components/override-dates";
import { getTaskContextByKey } from "../../../task-list/lib/taskData";
import { validateInput } from "../../../../shared/validation";
import { addImageToProduct } from "../lib/upcImageData";
import { SHIPMENT_TYPE_MAP } from "../../../shipments/lib/constants";
import { DATE_FNS_SHORT_DATE_FORMAT } from "../../../../shared/constants";
import { format } from "date-fns";
import ShipmentProductionOptionStore from "../../lib/shipmentProductOptionStore";
import { lookupUUID } from "shared/attribute-uuid";
import _ from "lodash";
import { getWeeklyProductUsage } from "../../../../shared/constants/sla-limits";

const LockableInput = Lockable(SuggestedCvid(Input));
const LockableInputList = Lockable(InputList);
const ValidatedInput = withValidate(Input);
var forceAdd = false;
var timeoutCvid = null;

const UpcForm = () => {
    const { viewOnlyShipment } = useContext(ViewOnlyPermissionsContext);
    const { bins, hasProducts, updateHasProducts, shipmentData, updateShipmentData } = useContext(ShipmentContext);
    const { upcs, attributeDefinitions, addUpc, updateUpc } = useContext(UpcsContext);
    const { settings } = useContext(SettingsContext);
    const [resetLockable, setResetLockable] = useState("");
    const [packageLevels, setPackageLevels] = useState([]);
    const [cvidList, setCvidList] = useState([]);
    const [showRelationship, setShowRelationship] = useState(false);
    const binTrackingNumber = bins.length === 1 ? bins[0] : "";
    const hasPremiumEditingShipmentDefault = ShipmentProductionOptionStore.useState((s) => s.hasDefaultPremiumEditing);
    const isFoodServiceDefault = ShipmentProductionOptionStore.useState((s) => s.isFoodService);
    const sendToGdsnDefault = ShipmentProductionOptionStore.useState((s) => s.sendToGdsn);
    const imagesNeedClientApproval = ShipmentProductionOptionStore.useState((s) => s.hasDefaultClientImageApproval);
    const modifierAttr = attributeDefinitions[lookupUUID("modifier")];
    const [isSubmitting, setIsSubmitting] = useState(false);
    const hasOverriddenProductLimit = useRef(false);
    const [formData, dispatch] = useReducer(reducer, {
        ...initialState,
        binTrackingNumber,
        hasDigitalContent: SHIPMENT_TYPE_MAP[shipmentData.shipmentType].isDigital,
        isFoodService: sendToGdsnDefault,
        sendToGdsn: isFoodServiceDefault,
        hasPremiumEditing: hasPremiumEditingShipmentDefault,
        imagesNeedClientApproval: imagesNeedClientApprovalDefault,
    });

    useEffect(() => {
        async function fetchData() {
            const packageLevelsData = await getPackageLevels();
            setPackageLevels(packageLevelsData);
        }

        fetchData();

        return () => {
            clearTimeout(timeoutCvid);
        };
    }, []);

    useEffect(() => {
        fetchSuggestedCvid();
    }, [formData.key]);

    useEffect(() => {
        if (!formData.key && hasPremiumEditingShipmentDefault !== formData.hasPremiumEditing) {
            dispatch({
                type: "UPDATE_FORM_DATA",
                name: "hasPremiumEditing",
                value: hasPremiumEditingShipmentDefault,
            });
        }
    }, [formData.key, hasPremiumEditingShipmentDefault]);

    useEffect(() => {
        if (!formData.key && imagesNeedClientApproval !== formData.imagesNeedClientApproval) {
            dispatch({
                type: "UPDATE_FORM_DATA",
                name: "imagesNeedClientApproval",
                value: imagesNeedClientApproval,
            });
        }
    }, [formData.key, imagesNeedClientApproval]);

    useEffect(() => {
        if (!formData.key && isFoodServiceDefault !== formData.isFoodService) {
            dispatch({
                type: "UPDATE_FORM_DATA",
                name: "isFoodService",
                value: isFoodServiceDefault,
            });
        }
    }, [formData.key, isFoodServiceDefault]);

    useEffect(() => {
        if (!formData.key && sendToGdsnDefault !== formData.sendToGdsn) {
            dispatch({
                type: "UPDATE_FORM_DATA",
                name: "sendToGdsn",
                value: sendToGdsnDefault,
            });
        }
    }, [formData.key, sendToGdsnDefault]);

    useEffect(() => {
        fetchSuggestedCvid();
    }, [formData.upc]);

    useEffect(() => {
        if ((!formData.upc || !formData.binTrackingNumber) && bins.length > 0) {
            const useBinTrackingNumber = bins.length === 1 ? bins[0] : "";
            dispatch({
                type: "UPDATE_FORM_DATA",
                name: "binTrackingNumber",
                value: useBinTrackingNumber,
            });
        }
    }, [bins]);

    useEffect(() => {
        const productsExist = upcs.length > 0;

        if (productsExist !== hasProducts) {
            updateHasProducts(productsExist);
        }

        eventEmitter.on("UPC_SELECT_EDIT", populateEditByKey);

        return () => {
            eventEmitter.removeListener("UPC_SELECT_EDIT", populateEditByKey);
        };
    }, [upcs]);

    const populateEditByKey = (key) => {
        if (!key) {
            resetFormData();
        }

        const upcForEdit = upcs.find((u) => u.key === key);

        if (upcForEdit) {
            dispatch({ type: "SET_FORM_DATA", formData: upcForEdit });
            setResetLockable(new Date().getTime());
        }
    };

    const fetchSuggestedCvid = () => {
        clearTimeout(timeoutCvid);
        timeoutCvid = setTimeout(async () => {
            const newCvidList = formData.upc ? await cvidLookup(formData.upc) : [];
            setCvidList(newCvidList);
        }, 700);
    };

    const validate = (data) => {
        if (!data.generateUpc && !data.upc) {
            notify.error("UPC# should be entered");

            return false;
        }

        if (!data.binTrackingNumber) {
            notify.error("Please select a Bin Tracking Number");

            return false;
        }

        const response = validateInput(data.modifier, modifierAttr.validation);

        if (response && !response.isValid) {
            notify.error(response.errorMsg);

            return false;
        }

        return true;
    };

    const resolveProductLimitOverride = async () => {
        if (!shipmentData.isCompleted) {
            return { override: null };
        }

        const productLimits = await getProductLimits(shipmentData.key, { isForNewUpc: true });

        if (!productLimits.isClientLimitExceeded) {
            return { override: null };
        }

        const confirmChange = await dialogConfirm({
            data: {
                suggested: productLimits.availableShipmentStartDate,
            },
            Component: OverrideDates,
        });

        if (confirmChange.cancel) {
            return { cancel: true };
        }

        return { override: confirmChange.update };
    };

    const validateWeeklyProductLimit = async () => {
        if (!shipmentData.cxHClientId || !shipmentData.startDate) {
            return {};
        }

        const limits = await getSLACounts(shipmentData.cxHClientId, format(new Date(shipmentData.startDate), DATE_FNS_SHORT_DATE_FORMAT));
        const isDigital = SHIPMENT_TYPE_MAP[shipmentData.shipmentType].isDigital;
        const { limit: weeklyLimit, count: weeklyCount } = getWeeklyProductUsage(limits, isDigital);

        if (weeklyLimit == null) {
            return {};
        }

        if (upcs.length >= weeklyLimit) {
            notify.errorResponse({
                message: "SLA limits exceeded",
                errors: [
                    {
                        field: "UPC count",
                        message: `Product limit exceeded. You cannot add more products because the client has reached their weekly product limit of ${weeklyLimit}.`,
                    },
                ],
            });

            return { blocked: true };
        }

        return { requiresOverride: weeklyCount >= weeklyLimit };
    };

    const saveUpc = async (overrideClientProductLimits = null, limitChecked = false) => {
        setIsSubmitting(true);
        const shipmentProducts = await getTaskContextByKey(shipmentData.key);
        const productKey = formData.key;

        const finalizedProduct = shipmentProducts.find((x) => x.shipmentProductKey === productKey);

        if (finalizedProduct && finalizedProduct.isFinalized) {
            setIsSubmitting(false);
            notify.error(`You cannot change a UPC that has been finalized.`);

            return;
        }

        const payload = {
            ...formData,
            barcodeType: !_.isEmpty(formData.barcodeType) ? formData.barcodeType : null,
            upc: formData.upc.trim(),
            modifier: formData.modifier ? formData.modifier : "",
            forceAdd: productKey ? true : forceAdd,
        };

        if (!validate(payload)) {
            setIsSubmitting(false);

            return;
        }

        // Only new UPCs are subject to the product limit check; skip when it was already resolved for this save.
        if (!productKey && !limitChecked) {
            const { blocked, requiresOverride } = await validateWeeklyProductLimit();

            if (blocked) {
                setIsSubmitting(false);

                return;
            }

            if (requiresOverride && !hasOverriddenProductLimit.current) {
                const { cancel, override } = await resolveProductLimitOverride();

                if (cancel) {
                    setIsSubmitting(false);

                    return;
                }

                if (override) {
                    hasOverriddenProductLimit.current = true;
                }

                overrideClientProductLimits = override;
            }
        }

        payload.overrideClientProductLimits = overrideClientProductLimits;

        const saveFunction = payload.key ? updateProduct : addProductToShipment;

        const response = await saveFunction(shipmentData.key, payload).catch(() => {
            setIsSubmitting(false);
            notify.error(DEFAULT_GENERIC_ERROR);
        });

        if (!response) {
            return;
        }

        if (response.message === "duplication") {
            const errorMessage = response.errors[0].message;
            const userResponse = await notify.confirm(errorMessage, {
                yesLabel: "Add",
                noLabel: "Cancel",
            });

            if (userResponse) {
                forceAdd = true;
                //Timing?
                saveUpc(overrideClientProductLimits, true);
            }

            setIsSubmitting(false);

            return;
        }

        if (!response || notify.errorResponse(response)) {
            setIsSubmitting(false);

            return;
        }

        let hasError = false;

        if (response.isDamaged && formData.damageImage) {
            const file = formData.damageImage;
            const imageData = new FormData();
            imageData.append("name", file.name);
            imageData.append("notes", response.notes);
            imageData.append("file", file);

            await uploadDamageImage(response.key, imageData).catch(() => {
                notify.error("Imaged failed to upload");
                resetFormData();

                if (response) {
                    dispatch({ type: "SET_FORM_DATA", formData: response });
                }

                hasError = true;
            });
        }

        if (hasError) {
            return;
        }

        if (productKey) {
            updateUpc(response);
            resetFormData();
        } else {
            await addUpc(response);
            eventEmitter.emit("UPDATE_SLA");
            eventEmitter.emit("ADD_DELETE_UPC", response);

            // Move: the BE moved the shipment to the next available week — refetch to pick up the new start date.
            if (overrideClientProductLimits === false) {
                const updatedShipment = await getShipment(shipmentData.key);
                updateShipmentData(updatedShipment);
            }
        }

        resetFormData();
        setIsSubmitting(false);

        notify.success("Saved!");
    };

    const updatePackageLevel = (name, value) => {
        const selectedPackage = packageLevels.find((l) => l.value === value) || null;
        updateFormData(name, selectedPackage);
    };

    const updateFieldConfirmBool = async (key, value) => {
        if (!value) {
            // Confirm
            const confirm = await notify.confirm("Are you sure this UPC does not have digital content?");

            if (!confirm) {
                return;
            }
        }

        updateFormData(key, value);
    };

    const uploadDamageImage = (productKey, payload) => {
        return addImageToProduct(shipmentData.key, productKey, payload);
    };

    const saveOnEnter = (e) => {
        if (e.which === KEYCODE.RETURN || e.keyCode === KEYCODE.RETURN) {
            handleSave();
        }
    };

    const handleSave = () => {
        saveUpc();
    };

    const updateGenerateUpc = async () => {
        const formChanges = {
            generateUpc: !formData.generateUpc,
            upc: "",
        };

        if (!formData.generateUpc && formData.upc) {
            const shouldRemoveUpc = await notify.confirm("Current UPC will be removed. Are you sure?");

            if (!shouldRemoveUpc) {
                return;
            }
        }

        dispatch({ type: "UPDATE_MANY_FORM_DATA", formChanges });
    };

    const updateFormData = (name, value) => {
        dispatch({ type: "UPDATE_FORM_DATA", name, value });
    };

    const resetFormData = () => {
        eventEmitter.emit("UPC_CANCEL_EDIT");

        const usebinTrackingNumber = bins.length === 1 ? bins[0] : "";

        dispatch({
            type: "RESET_FORM_DATA",
            binTrackingNumber: usebinTrackingNumber,
            hasDigitalContent: SHIPMENT_TYPE_MAP[shipmentData.shipmentType].isDigital,
            hasPremiumEditing: hasPremiumEditingShipmentDefault,
            imagesNeedClientApproval: imagesNeedClientApproval,
            isFoodService: isFoodServiceDefault,
            sendToGdsn: sendToGdsnDefault,
        });
        setResetLockable(new Date().getTime());

        if (forceAdd) {
            forceAdd = false;
        }
    };

    const binList = bins.map((bin) => {
        return { text: bin, value: bin };
    });
    const submitText = formData.key ? "Update" : "Add UPC to Shipment";
    const addImage = formData.isDamaged ? (
        <div className="fieldset-content">
            <UpcImageUpload updateState={updateFormData} />
        </div>
    ) : (
        ""
    );
    const packageHierarchy = showRelationship ? (
        <div className="fieldset-content">
            <PackageHierarchy
                key={`packageHierarchy_${formData.key}`}
                upcs={upcs}
                childKeys={formData.childProductKeys}
                parentKeys={formData.parentProductKeys}
                currentPackageLevel={formData.packageLevel}
                currentKey={formData.key}
                updateState={updateFormData}
            />
        </div>
    ) : null;
    const modifierValidation = formData.modifierAttr ? { validation: formData.modifierAttr.validation } : {};
    const buttonClearText = formData.key ? "Cancel" : "Reset";
    const packageLevel = formData.packageLevel && formData.packageLevel.value ? formData.packageLevel.value : "";
    const maxUPCsReached = !formData.key && settings.shipmentSettings && settings.shipmentSettings.maxNumUpcsPerShipment <= upcs.length;

    return (
        <div className="upc-form">
            <h3>UPC Details</h3>
            <div className="input-group">
                <div className="input-group -upc">
                    <button
                        title="Generate UPC"
                        className={classnames("button", { "-active": formData.generateUpc })}
                        onClick={updateGenerateUpc}
                    >
                        <FontAwesomeIcon icon={faBarcode} />
                    </button>
                    <Input
                        required={upcs.length === 0}
                        key="upc123"
                        id="up123"
                        label="UPC#"
                        name="upc"
                        value={formData.upc}
                        updateOnChange={updateFormData}
                        onKeyPress={saveOnEnter}
                        maxLength="14"
                        disabled={formData.generateUpc}
                    />
                </div>
                <ValidatedInput
                    label="Modifier"
                    name="modifier"
                    value={formData.modifier}
                    updateOnChange={updateFormData}
                    onKeyPress={saveOnEnter}
                    {...modifierValidation}
                    className="-small"
                />
            </div>

            <Picklist
                name="barcodeType"
                label={attributeDefinitions[lookupUUID("gs1BarcodeType")].name}
                defaultValue={formData.barcodeType}
                updateOnChange={(name, value = {}) => updateFormData(name, value)}
                provider={`/picklists/${attributeDefinitions[lookupUUID("gs1BarcodeType")].additionalInfo.picklist.name}`}
            />

            <LockableInput
                label="Internal Supplier ID"
                name="cvid"
                value={formData.cvid}
                updateOnChange={updateFormData}
                onKeyPress={saveOnEnter}
                lockable={true}
                reset={resetLockable}
                suggestions={cvidList}
                dataType="String"
            />

            <LockableInputList
                label="Internal Supplier ID Extras"
                name="shipmentProductInternalIdExtras"
                defaultValue={formData.shipmentProductInternalIdExtras}
                updateOnChange={updateFormData}
                lockable={true}
                reset={resetLockable}
                dataType="StringList"
            />
            <br />
            {SHIPMENT_TYPE_MAP[shipmentData.shipmentType].isDigital && (
                <Checkbox
                    label="Has Digital Content"
                    name="hasDigitalContent"
                    value={formData.hasDigitalContent}
                    updateOnChange={updateFieldConfirmBool}
                />
            )}
            <Checkbox
                label="Is a Sample"
                name="isSample"
                value={formData.isSample}
                updateOnChange={updateFormData}
                disabled={viewOnlyShipment.disableSalesSample}
            />
            <Checkbox
                label="Foodservice"
                name="isUpcFoodService"
                value={formData.isFoodService}
                updateOnChange={(name, value) => updateFormData("isFoodService", value)}
            />
            <Checkbox
                label="Send to GDSN"
                name="sendToGdsn"
                value={formData.sendToGdsn}
                updateOnChange={(name, value) => updateFormData("sendToGdsn", value)}
            />
            <Checkbox
                label="Premium Editing"
                name="hasPremiumEditing"
                value={formData.hasPremiumEditing}
                updateOnChange={updateFormData}
            />
            <Checkbox
                label="Client Image Approval"
                name="imagesNeedClientApproval"
                value={formData.imagesNeedClientApproval}
                updateOnChange={updateFormData}
            />

            <Select
                required
                label="Bin#"
                options={binList}
                name="binTrackingNumber"
                value={formData.binTrackingNumber}
                updateOnChange={updateFormData}
            />

            <Select
                label="Package Level"
                options={packageLevels}
                name="packageLevel"
                value={packageLevel}
                updateOnChange={updatePackageLevel}
            />

            <fieldset className="fieldset-hider">
                <legend>
                    <Checkbox
                        label="Package Hierarchy"
                        name="showRelationship"
                        value={showRelationship}
                        updateOnChange={(name, value) => {
                            setShowRelationship(value);
                        }}
                    />
                </legend>
                {packageHierarchy}
            </fieldset>

            <fieldset className="fieldset-hider">
                <legend>
                    <Checkbox
                        label="Is Damaged"
                        name="isDamaged"
                        value={formData.isDamaged}
                        updateOnChange={updateFormData}
                    />
                </legend>
                {addImage}
            </fieldset>

            <div className="button-group -right -top">
                <button
                    className="button"
                    onClick={resetFormData}
                >
                    {buttonClearText}
                </button>
                <div className="flex-column txt-right">
                    <LoaderButton
                        className="button -primary"
                        onClick={handleSave}
                        isSubmitting={isSubmitting}
                        text={submitText}
                        disabled={maxUPCsReached}
                    />
                    {maxUPCsReached && <span className="txt-disabled">Maximum of {settings.shipmentSettings.maxNumUpcsPerShipment} UPCs added.</span>}
                </div>
            </div>
        </div>
    );
};

export default UpcForm;
